<template>
    <b-form
        ref="feedbackForm"
        id="feedback-form"
        name="feedback-form"
        enctype="multipart/form-data"
        v-on:submit.prevent="$_onSubmit"
    >
        <!-- Form content -->
        <div id="feedback_panel_content">
            <pre v-if="$store.getters.showDevInfo">
                measure: {{ isMeasure }}
                edit: {{ feedbackModify }}
                add: {{ feedbackAdd }}
                hasChangedShape: {{ hasChangedShape }}
                hasModifiableFields: {{ hasModifiableFields }}
                geometryType: {{ geometryType }}
            </pre>
            <!-- Location data -->
            <div v-if="hasAddress" class="form-group">
                <h4>{{ $t('location') }}</h4>
                <div class="feedback-location-icon">
                    <img
                        src="~@/assets/imported-icons/ic_fluent_location_20_regular.svg"
                        alt="feedbacklocation"
                    />
                </div>
                <div class="d-inline-block">
                    <label>{{ $t('address') }}</label>
                    <b-spinner
                        v-if="!lastFeedbackAddress"
                        small
                        class="d-flex"
                        label="Loading"
                    />
                    <template v-else>
                        <span class="span-readonly">{{
                            lastFeedbackAddress
                        }}</span>
                    </template>
                </div>
            </div>
            <!-- Measure readonly fields -->
            <template v-if="isMeasure">
                <div
                    v-for="field in editFeedbackFields"
                    :key="`field_name_${field.id}`"
                >
                    <div
                        class="form-group"
                        v-if="$_isMeasureFieldWithValue(field)"
                    >
                        <label :for="field.name">{{ field.alias }}</label>
                        <span
                            v-if="field.name === 'area'"
                            :id="field.name"
                            class="span-readonly"
                            ><area-text :value="Number(field.value)"
                        /></span>
                        <span
                            v-else-if="field.name === 'length'"
                            :id="field.name"
                            class="span-readonly"
                            ><length-text :value="Number(field.value)"
                        /></span>
                        <span v-else :id="field.name" class="span-readonly">{{
                            field.value
                        }}</span>
                    </div>
                </div>
            </template>
            <!-- Non personal data -->
            <div v-for="field in editFeedbackFields" :key="field.id">
                <div
                    v-if="
                        field.visible ||
                        field.name === 'meldingsnummer' ||
                        (feedbackModify && !field.personalData)
                    "
                >
                    <!-- Meldingsnummer-->
                    <div
                        class="form-group"
                        v-if="
                            field.type === fieldTypeConst.GENERIC &&
                            field.name === 'meldingsnummer' &&
                            field.value
                        "
                    >
                        <label v-if="!isMobile" :for="field.name"
                            >Aansluiten bij
                            {{ field.alias.toLowerCase() }}</label
                        >
                        <span :id="field.name" class="span-readonly">{{
                            field.value
                        }}</span>
                    </div>
                    <!-- Numeric -->
                    <div
                        v-if="
                            field.type === fieldTypeConst.NUMERIC &&
                            (!field.personalData || feedbackModify)
                        "
                    >
                        <x-number
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field, true, false, true)"
                            :placeholder="field.placeholder"
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    true
                                )
                            "
                            :state="field.state"
                            :step="1"
                            :minValue="$_getMinMaxValue(field, 'min')"
                            :maxValue="$_getMinMaxValue(field, 'max')"
                            :maxLength="$_getMaxLength(field)"
                            :value="parseInt(field.value)"
                            @input="$_updateCalculatedFields($event, field)"
                        >
                        </x-number>
                    </div>
                    <!-- Numeric with decimals -->
                    <div
                        v-if="
                            field.type === fieldTypeConst.NUMERICWITHDECIMALS &&
                            (!field.personalData || feedbackModify)
                        "
                    >
                        <x-number
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field, true, false, true)"
                            :placeholder="field.placeholder"
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    true
                                )
                            "
                            :state="field.state"
                            :step="0"
                            :minValue="$_getMinMaxValue(field, 'min')"
                            :maxValue="$_getMinMaxValue(field, 'max')"
                            :maxLength="$_getMaxLength(field)"
                            :value="parseFloat(field.value)"
                            :allowDecimals="true"
                            @input="$_updateCalculatedFields($event, field)"
                        >
                        </x-number>
                    </div>
                    <!-- Alphanumeric -->
                    <!-- Show State field only when in mobile and when editing an existing feedback record-->
                    <div
                        v-if="
                            (field.type === fieldTypeConst.ALPHANUMERIC &&
                                (!field.personalData || feedbackModify)) ||
                            (isMobile &&
                                field.type === fieldTypeConst.STATE &&
                                feedbackModify)
                        "
                    >
                        <x-input
                            v-if="field.domain === null && field.nrRows <= 1"
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field, true, false, true)"
                            :placeholder="field.placeholder"
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    true
                                )
                            "
                            :state="field.state"
                            :value="field.value"
                            :maxlength="$_getMaxLength(field)"
                            v-model="field.value"
                            :justInvalidState="true"
                        >
                        </x-input>
                        <!-- Textarea -->
                        <x-textarea
                            v-if="field.domain === null && field.nrRows > 1"
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field)"
                            :rows="field.nrRows"
                            :maxlength="$_getMaxLength(field)"
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    true
                                )
                            "
                            :state="field.state"
                            :placeholder="field.placeholder"
                            :value="field.value"
                            v-model="field.value"
                        >
                        </x-textarea>
                        <!-- Domain dropdown list -->
                        <x-select
                            v-if="
                                field.domain &&
                                field.domainType ===
                                    domainTypeConst.DROPDOWNLIST
                            "
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field)"
                            :options="$_getDomainValues(field.domain.keyvalues)"
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    true
                                )
                            "
                            :state="field.state"
                            :value="field.value"
                            @selected="onXSelectSelected($event, field)"
                            :simpleMode="true"
                        >
                        </x-select>
                        <!-- Domain suggestion list -->
                        <x-datalist
                            v-if="
                                field.domain &&
                                field.domainType ===
                                    domainTypeConst.SUGGESTIONLIST
                            "
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field)"
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    true
                                )
                            "
                            :state="field.state"
                            :invalidMessage="field.invalidFieldError"
                            :options="$_getDomainValues(field.domain.keyvalues)"
                            :value="field.domainKeyValue"
                            @change="onXDatailListInput($event, field)"
                            :maxlength="$_getMaxLength(field)"
                            :simpleMode="true"
                            autocomplete="off"
                        >
                        </x-datalist>
                    </div>
                    <!-- Add domain value to dropdown list -->
                    <add-domain-value
                        v-if="
                            field.domain &&
                            (field.domainType ===
                                domainTypeConst.DROPDOWNLIST ||
                                field.domainType ===
                                    domainTypeConst.SUGGESTIONLIST) &&
                            field.domainExtendable
                        "
                        :name="field.name"
                        :domainValues="field.domain.keyvalues"
                        :domainValueMaxLength="field.maxLength"
                        @updateDomainValue="$_updateDomainValue(field, $event)"
                        @updateKeyvalues="$_updateKeyvalues(field, $event)"
                    >
                    </add-domain-value>
                    <!-- Photo for mutating feedback -->
                    <div
                        v-if="
                            feedbackModify &&
                            !isMultiFeedbackEdit &&
                            field.type === fieldTypeConst.FOTO &&
                            !field.personalData
                        "
                    >
                        <!-- Show foto if available -->
                        <div
                            v-if="
                                showImagePreview &&
                                field.value &&
                                field.value.length > 1
                            "
                        >
                            <feedback-image-viewer
                                :id="field.name"
                                :name="field.name"
                                :text="$_getFieldTitle(field)"
                                :value="$_getImagePreviewUrl(field.value)"
                                :enableRemove="true"
                                @removed="$_onImageRemoved"
                            >
                            </feedback-image-viewer>
                        </div>
                        <!-- Show add foto controls if no photo is set or keepfile is false only if it's NOT a webfeedback! -->
                        <div
                            v-if="
                                !showImagePreview ||
                                !field.value ||
                                field.value.length === 0
                            "
                        >
                            <upload-foto
                                :id="field.name"
                                :name="field.name"
                                :text="$_getFieldTitle(field)"
                                @selected="$_onImageSelected"
                                :state="field.state"
                            >
                            </upload-foto>
                        </div>
                    </div>
                    <!-- Add photo only if it's NOT a webfeedback! -->
                    <div
                        v-if="
                            feedbackAdd &&
                            field.type === fieldTypeConst.FOTO &&
                            !field.personalData
                        "
                    >
                        <upload-foto
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field)"
                            @selected="$_onImageSelected"
                            :state="field.state"
                        >
                        </upload-foto>
                    </div>
                    <!-- Date field -->
                    <div
                        v-if="
                            field.type === fieldTypeConst.DATE &&
                            !field.personalData
                        "
                        style="margin-bottom: 10px"
                    >
                        <x-input
                            :id="field.name"
                            :name="field.name"
                            :text="$_getFieldTitle(field)"
                            :placeholder="
                                field.placeholder
                                    ? field.placeholder
                                    : 'dd-mm-jjjj'
                            "
                            :required="
                                $_getFieldRequiredState(
                                    field,
                                    true,
                                    false,
                                    false
                                )
                            "
                            :state="field.state"
                            :invalidMessage="invalidFieldValueErrorMessage"
                            :value="field.value"
                            @change="$_validateDate(field)"
                            v-model="field.value"
                            :justInvalidState="true"
                        >
                        </x-input>
                    </div>
                    <!-- Category field -->
                    <x-select
                        v-if="
                            field.type === fieldTypeConst.CATEGORY &&
                            !field.personalData
                        "
                        :id="field.name"
                        :name="field.name"
                        :text="$_getFieldTitle(field)"
                        :options="
                            $_getCategoryValues(
                                field.feedbackFieldCategoryLookups
                            )
                        "
                        :required="
                            $_getFieldRequiredState(field, true, false, false)
                        "
                        :state="field.state"
                        :disabled="feedbackAdd && feedbackJoin"
                        :extendedClass="{
                            plaintext: feedbackAdd && feedbackJoin
                        }"
                        :value="field.value"
                        @selected="onXSelectSelected($event, field)"
                        :simpleMode="true"
                    >
                    </x-select>
                    <!-- Email -->
                    <x-input
                        v-if="
                            field.type === fieldTypeConst.EMAIL &&
                            feedbackModify
                        "
                        :id="field.name"
                        :name="field.name"
                        :text="$_getFieldTitle(field, true, false, true)"
                        :placeholder="field.placeholder"
                        :required="
                            $_getFieldRequiredState(field, true, false, true)
                        "
                        :state="field.state"
                        :value="field.value"
                        v-model="field.value"
                        :invalidMessage="
                            field.state === true ? '' : $t('userIncorrectEmail')
                        "
                        @change="$_validateEmailValue(field)"
                        :justInvalidState="true"
                    >
                    </x-input>
                    <!-- Calculated Field -->
                    <!-- State is set to false to allow for a invalidMessage to show up. As the field  plaintext this -->
                    <!-- does not affect the visible properties of the field.                                         -->
                    <!-- This is a workaround as updating field.state in the validation function has no effect         -->
                    <x-input
                        v-if="field.type === fieldTypeConst.EXPRESSION"
                        :id="field.name"
                        :name="field.name"
                        :text="$_getFieldTitle(field, true, false, true)"
                        :placeholder="field.placeholder"
                        :state="false"
                        :value="field.value"
                        v-model="field.value"
                        :plaintext="true"
                        :invalidMessage="$_invalidExpression(field)"
                    >
                    </x-input>
                </div>
            </div>
            <!-- Web-feedback personal data -->
            <div v-if="hasPersonalDataFields" class="personal-data">
                <x-checkbox
                    id="use-personal-data"
                    name="use-personal-data"
                    :text="usePersonalDataLabel"
                    v-model="feedbackData.feedbackTheme.usePersonalData"
                    @change="$_changeUsePersonalData"
                >
                </x-checkbox>
                <div
                    v-show="feedbackData.feedbackTheme.usePersonalData"
                    class="form-group"
                    v-for="field in editFeedbackFields"
                    :key="field.id"
                >
                    <!-- Web-feedback personal info fields -->
                    <div v-show="field.visible">
                        <div
                            v-if="field.personalData"
                            style="margin-bottom: 10px"
                        >
                            <!-- Alphanumeric or numeric -->
                            <x-input
                                v-if="
                                    field.domain === null && field.nrRows <= 1
                                "
                                :id="field.name"
                                :name="field.name"
                                :text="
                                    $_getFieldTitle(field, false, true, false)
                                "
                                :placeholder="field.placeholder"
                                :required="
                                    $_getFieldRequiredState(
                                        field,
                                        false,
                                        true,
                                        false
                                    )
                                "
                                :state="field.state"
                                :invalidMessage="
                                    field.state === true
                                        ? ''
                                        : $t('userIncorrectEmail')
                                "
                                :maxlength="$_getMaxLength(field)"
                                v-model="field.value"
                                @change="$_validateEmailValue(field)"
                                :justInvalidState="true"
                            >
                            </x-input>
                            <!-- Textarea -->
                            <x-textarea
                                v-if="field.domain === null && field.nrRows > 1"
                                :id="field.name"
                                :name="field.name"
                                :text="
                                    $_getFieldTitle(field, false, true, false)
                                "
                                :rows="field.nrRows"
                                :maxlength="$_getMaxLength(field)"
                                :required="
                                    $_getFieldRequiredState(
                                        field,
                                        false,
                                        true,
                                        false
                                    )
                                "
                                :state="field.state"
                                :placeholder="field.placeholder"
                            >
                            </x-textarea>
                            <!-- Domain dropdown list -->
                            <x-select
                                v-if="
                                    field.domain &&
                                    field.domainType ===
                                        domainTypeConst.DROPDOWNLIST
                                "
                                :id="field.name"
                                :name="field.name"
                                :text="
                                    $_getFieldTitle(field, false, true, false)
                                "
                                :options="
                                    $_getDomainValues(field.domain.keyvalues)
                                "
                                :required="
                                    $_getFieldRequiredState(
                                        field,
                                        false,
                                        true,
                                        false
                                    )
                                "
                                :state="field.state"
                                :value="field.value"
                                @selected="onXSelectSelected($event, field)"
                                :simpleMode="true"
                            >
                            </x-select>
                            <!-- Domain suggestion list -->
                            <x-datalist
                                v-if="
                                    field.domain &&
                                    field.domainType ===
                                        domainTypeConst.SUGGESTIONLIST
                                "
                                :id="field.name"
                                :name="field.name"
                                :text="$_getFieldTitle(field)"
                                :required="$_getFieldRequiredState(field)"
                                :state="field.state"
                                :invalidMessage="field.invalidFieldError"
                                :options="
                                    $_getDomainValues(field.domain.keyvalues)
                                "
                                :maxlength="$_getMaxLength(field)"
                                v-model="field.domainKeyValue"
                                :simpleMode="true"
                                autocomplete="off"
                            >
                            </x-datalist>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- Buttons -->
        <div class="sticky-bottom fixed-bottom">
            <b-button
                v-if="showSaveButton"
                variant="primary"
                type="submit"
                :class="{ saving: isSaving }"
            >
                <span v-if="!isSaving">{{ saveButtonText }}</span>
                <span
                    :class="{ 'fas fa-sync fa-spin': isSaving }"
                    v-if="isSaving"
                ></span>
            </b-button>
            <b-button
                @click="$_onClickSetLocationButton"
                class="ml-2"
                :class="{ saving: isSaving }"
                v-show="showSetLocationButton"
            >
                <span
                    :class="{ 'fas fa-sync fa-spin': isSaving }"
                    v-if="isSaving && !hasModifiableFields"
                ></span>
                {{ titleLocationButton }}
            </b-button>
            <b-button
                :disabled="isSaving"
                @click="$_onCancel"
                class="ml-2"
                :class="{ saving: isSaving }"
            >
                {{ $t('btnCancelLabel') }}
            </b-button>
        </div>
    </b-form>
</template>

<script>
    import * as moment from 'moment';
    import { sortBy } from 'lodash';
    import { mapActions, mapGetters } from 'vuex';

    import {
        validateDate,
        validateNumber,
        validateEmail
    } from '../../../services/utilities/validator';
    import {
        getCreateFeedbackData,
        getMinMaxValueForInputNumber,
        getVariablesFromString,
        evaluateNumericExpression,
        getImageUrlFromValue
    } from '../../../services/utilities';

    import {
        CONTENT_TYPE_ID,
        DOMAIN_TYPE,
        FIELD_TYPE,
        MAX_LENGTH_CONSTRAINTS,
        NEDGLOBE_VIEWER,
        DEFAULT_ANIMATION_MS,
        FEEDBACKS_GEOMETRY_TYPES,
        PANEL_WIDTHS
    } from '../../../constants';

    import UploadFoto from '../../UploadFoto.vue';
    import FeedbackImageViewer from '../../FeedbackImageViewer.vue';
    import AddDomainValue from '../../BaseComponents/AddDomainValue.vue';
    import { isSmallDevice } from '../../../services/utilities/devices';
    import { getLocationString } from '../../../services/feedback';

    import AreaText from '../display/AreaText';
    import LengthText from '../display/LengthText';

    export default {
        name: 'FeedbackForm',

        components: {
            'upload-foto': UploadFoto,
            'feedback-image-viewer': FeedbackImageViewer,
            'length-text': LengthText,
            'area-text': AreaText,
            'add-domain-value': AddDomainValue
        },

        props: {
            feedbackThemeObject: { type: Object, required: true },
            feedbackObject: { type: Object, required: true },
            meldingsData: { type: Object, required: false },
            isEditing: { type: Boolean, default: false }
        },

        async created() {
            const localData = JSON.parse(
                JSON.stringify(this.feedbackThemeObject.data)
            );
            this.feedbackData = { feedbackTheme: { ...localData } };
            await this.$_initFeedBackObject(this.feedbackObject);
            if (this.hasAddress) {
                this.lastFeedbackAddress = await this.$_getAddress(
                    this.feedbackObject.featureObject.location
                );
            }
            if (this.feedbackModify) {
                this.$_initializeModifyFeedbackData();
            }
            if (
                !this.feedbackModify &&
                !this.hasModifiableFields &&
                !this.lastFeedbackAddress
            ) {
                this.$_onSubmit();
            } else {
                this.$_setValueAutoDataFields(this.feedbackData);
                if (!this.feedbackModify) {
                    this.$_setDefaultFeedbackFieldValues();
                }
            }
        },

        destroyed() {
            const customLayerName = NEDGLOBE_VIEWER.customLayers.drawing;
            this.$store.commit('RESET_MAP_FEATURES', {
                mapName: 'main',
                customLayerName
            });
        },

        data() {
            return {
                feedbackData: null,
                // Constants
                fieldTypeConst: FIELD_TYPE,
                domainTypeConst: DOMAIN_TYPE,

                // variables
                lastFeedbackAddress: null,

                // feedback add id = -1
                featureId: '-1',

                error: null,
                text: '',
                invalidFieldValueErrorMessage: '',
                isSaving: false,

                // if an image is selected, don't show preview
                showImagePreview: true,

                resendButtonText: 'Verwerken offline',
                usePersonalDataLabel:
                    'Ik geef toestemming om mijn contactgegevens te gebruiken (deze worden niet openbaar gemaakt)',
                anonymousFeedback: true,

                joinExistingFeedback: null,
                feedbackShape: null,
                editFeedbackFields: null,
                origCategory: null,
                origState: null,
                origFotoValue: []
            };
        },

        computed: {
            ...mapGetters([
                'isMobile',
                'getMainMap',
                'getUserData',
                'getUserInfo',
                'getOrganizationId',
                'getLocale'
            ]),
            titleLocationButton() {
                return this.geometryType === FEEDBACKS_GEOMETRY_TYPES.POINT
                    ? this.$t('changeLocation')
                    : this.$t('changeGeometry');
            },
            feedbackTheme() {
                return this.feedbackData?.feedbackTheme;
            },
            feedbackGeometryType: function () {
                return this.feedbackTheme?.feedbackType?.geometryType;
            },
            isMeasure() {
                return (
                    this.feedbackTheme?.feedbackType?.contentTypeId ===
                    CONTENT_TYPE_ID.MEASUREMENT
                );
            },
            hasChangedShape() {
                return this.feedbackObject.updated === true;
            },
            feedbackModify() {
                return this.isEditing;
            },
            feedbackAdd() {
                return !this.feedbackModify;
            },
            geometryType() {
                return this.feedbackShape?.featureObject?.geometryType;
            },
            userInfo() {
                return this.isMobile ? this.getUserData : this.getUserInfo;
            },
            feedbackJoin() {
                return !!this.meldingsData?.meldingsnummer;
            },

            /**
             * update feedbackFields array with prefilled values for teurgmeldingen in web
             * @return {array} [feedbackFields array]
             */
            feedbackFields() {
                if (this.feedbackTheme?.feedbackType) {
                    let feedbackFields =
                        this.feedbackTheme.feedbackType.feedbackFields;
                    if (this.meldingsData) {
                        feedbackFields = feedbackFields.map((field) => {
                            const meldingDataField = this.meldingsData[field.name];
                            if (
                                meldingDataField &&
                                (field.name === 'meldingsnummer' ||
                                    field.name === 'adres' ||
                                    field.type === FIELD_TYPE.CATEGORY)
                            ) {
                                return { ...field, value: meldingDataField.value };
                            }
                            return field;
                        });
                    } else if (this.feedbackObject?.data) {
                        feedbackFields = feedbackFields.map((field) => {
                            const meldingDataField =
                                this.feedbackObject.data[field.name];
                            if (meldingDataField) {
                                return { ...field, value: meldingDataField };
                            }
                            return field;
                        });
                    }
                    return feedbackFields;
                }
                return null;
            },

            /**
             * Sort feedbackFields array on its 'order' field
             * @return {array} [ordered feedbackFields array]
             */
            orderedFeedbackFields() {
                if (this.feedbackObject.editFeedbackFields) {
                    return this.feedbackObject.editFeedbackFields;
                }
                if (this.feedbackFields) {
                    return sortBy(this.feedbackFields, [(o) => o.order]);
                }
                return [];
            },

            /**
             * Does the feedbackFields array contain items with the personalData == true
             * @return {Boolean} [true if there are personalData == true items, false otherwise]
             */
            hasPersonalDataFields() {
                if (!this.isMobile && this.feedbackTheme?.feedbackType) {
                    return this.feedbackTheme.feedbackType.feedbackFields.some(
                        (item) => {
                            return item.personalData;
                        }
                    );
                }
                return false;
            },

            /**
             * Does the feedback has any field that can be filled in?
             * @return {Boolean} [true if there is a field that can be filled in, false otherwise]
             */
            hasModifiableFields() {
                if (this.feedbackTheme?.feedbackType) {
                    const genericFields =
                        this.feedbackTheme?.feedbackType.feedbackFields.filter(
                            (ff) => ff.type === FIELD_TYPE.GENERIC
                        );
                    return (
                        genericFields.length !==
                        this.feedbackTheme.feedbackType.feedbackFields.length
                    );
                }
                return true;
            },

            hasAddress() {
                if (this.feedbackTheme?.feedbackType) {
                    const isPoint =
                        this.feedbackObject.featureObject.geometryType ===
                        FEEDBACKS_GEOMETRY_TYPES.POINT;
                    const hasAddressField =
                        this.feedbackTheme.feedbackType.feedbackFields.find(
                            (field) => field.name === 'adres'
                        );
                    return isPoint && hasAddressField && this.locationExisting;
                }
                return false;
            },

            /**
             * Always show save button, except when it is a measurement with no modifiable fields
             * @return {Boolean} [true if save button must be shown, false otherwise]
             */
            showSaveButton() {
                return !(this.isMeasure && !this.hasModifiableFields);
            },

            /**
             * Text on Save button
             * @return {string} [text]
             */
            saveButtonText() {
                if (
                    this.feedbackTheme?.feedbackType &&
                    this.feedbackTheme.feedbackType.useInWebTheme &&
                    !this.isMobile &&
                    !this.isMeasure
                ) {
                    return this.$t('buttonNameSend');
                } else {
                    return this.$t('btnSaveLabel');
                }
            },
            locationExisting() {
                return this.feedbackObject.featureObject.location !== null;
            },
            showSetLocationButton() {
                return this.feedbackModify && this.locationExisting;
            },
            isMultiFeedbackEdit() {
                const singleFeature =
                    this.feedbackShape.featureObject.features == null;
                return !singleFeature;
            }
        },
        watch: {
            async feedbackObject(newVal) {
                this.$_initFeedBackObject(newVal);
                if (this.feedbackModify && this.hasChangedShape) {
                    if (this.hasAddress) {
                        this.lastFeedbackAddress = await this.$_getAddress(
                            this.feedbackObject.featureObject.location
                        );
                    }
                    if (!this.hasModifiableFields && !this.lastFeedbackAddress) {
                        this.$_onSubmit();
                    }
                }
            },
            orderedFeedbackFields(newVal) {
                this.editFeedbackFields = JSON.parse(JSON.stringify(newVal));
                // To check for changed values at the backend, save the original value for sending later as the oldValue
                this.origCategory = this.$_getFieldCopy(FIELD_TYPE.CATEGORY);
                this.origState = this.$_getFieldCopy(FIELD_TYPE.STATE);

                // Save the old foto name so we can delete the old file in the backend when
                // user removes the foto or selects a new foto.
                for (const field of this.editFeedbackFields.filter(
                    (f) => f.type === FIELD_TYPE.FOTO
                )) {
                    this.origFotoValue[field.name] = field.value;
                }
            },
            feedbackShape() {
                this.$_setValueAutoDataFields();
            }
        },
        methods: {
            ...mapActions(['getAddressInfo']),
            async $_initFeedBackObject(feedbackObject) {
                this.feedbackShape = feedbackObject;
                await this.$store.dispatch('loadFeedback', {
                    feedbackId: this.feedbackThemeObject.id
                });
                const { computedStyles } = this.$store.getters.getLayerById(
                    this.feedbackThemeObject.layerId
                ).extended;
                const customLayerName = NEDGLOBE_VIEWER.customLayers.drawing;
                this.$store.commit('SHOW_MAP_FEATURE', {
                    mapName: 'main',
                    customLayerName,
                    features: this.isMultiFeedbackEdit
                        ? this.feedbackShape.featureObject.features
                        : null,
                    feature: !this.isMultiFeedbackEdit
                        ? this.feedbackShape.featureObject.feature
                        : null,
                    computedStyles,
                    styleType: NEDGLOBE_VIEWER.styleDisplayTypes.selected,
                    reset: true
                });
                if (!this.feedbackModify && this.hasModifiableFields) {
                    const margin = 100;
                    const popupWidth = PANEL_WIDTHS.FEEDBACK;
                    const marginRight = popupWidth + margin;
                    const currentResolution = this.getMainMap?.viewport?.resolution;

                    // Do not zoom to features on a small device.
                    if (!isSmallDevice()) {
                        let features = this.isMultiFeedbackEdit
                            ? this.feedbackShape.featureObject.features
                            : [this.feedbackShape.featureObject.feature];
                        this.$store.dispatch('zoomToFeatures', {
                            mapName: 'main',
                            features: features,
                            zoomWhenOutOfView: false,
                            options: {
                                padding: [margin, marginRight, margin, margin],
                                duration: DEFAULT_ANIMATION_MS
                            },
                            minResolution: currentResolution
                        });
                    }
                }
                this.lastFeedbackAddress = null;
            },

            // find a field of a given type and return a deep copy
            $_getFieldCopy(fieldType) {
                const field = this.editFeedbackFields.find(
                    (f) => f.type === fieldType
                );
                if (field) {
                    return { ...field };
                } else {
                    return null;
                }
            },

            $_isMeasureFieldWithValue(field) {
                return (
                    field.value !== null &&
                    ['length', 'area', 'coord_x', 'coord_y'].includes(
                        field.name.toLowerCase()
                    )
                );
            },

            /**
             * Get the title for a input field.
             * @param  {object} field [filed object]
             * @param  {boolean} requiredWhenVisible - field is only required when visible
             * @param  {boolean} requiredWhenUsingPersonalData - field is only required when using personal data
             * @param  {boolean} requiredUnlessModifyingPersonalData - field is not required when personalData in modify form
             * @return {string}       [field title]
             */
            $_getFieldTitle(
                field,
                requiredWhenVisible = false,
                requiredWhenUsingPersonalData = false,
                requiredUnlessModifyingPersonalData = false
            ) {
                let required = this.$_getFieldRequiredState(
                    field,
                    requiredWhenVisible,
                    requiredWhenUsingPersonalData,
                    requiredUnlessModifyingPersonalData
                );

                // calculated field should never get (optional) in it's title
                // set required to true
                if (field.type === this.fieldTypeConst.EXPRESSION) {
                    required = true;
                }

                return required
                    ? `${field.alias}`
                    : `${field.alias}${this.$t('labelOptional')}`;
            },

            /**
             * Get the url for an image field.
             * @param  {string} value field from data object
             * @return {string} preview url
             */
            $_getImagePreviewUrl(value) {
                if (value) {
                    return getImageUrlFromValue(value);
                }
                return value;
            },

            /**
             * Determine if the field is required
             * A required and visible field is required, unless it is personal data that is being modified in Mobile
             * @param field
             * @return {boolean} - true if field is required
             */
            $_getFieldRequiredState(
                field,
                requiredWhenVisible = false,
                requiredWhenUsingPersonalData = false,
                requiredUnlessModifyingPersonalData = false
            ) {
                if (this.isMultiFeedbackEdit) {
                    return false;
                }
                let isRequired = field.required;
                if (requiredWhenVisible) {
                    isRequired = isRequired && field.visible;
                }
                if (requiredWhenUsingPersonalData && field.personalData) {
                    isRequired =
                        isRequired &&
                        this.feedbackData.feedbackTheme.usePersonalData;
                }
                if (requiredUnlessModifyingPersonalData) {
                    isRequired =
                        isRequired && !(field.personalData && this.feedbackModify);
                }
                return isRequired;
            },

            /**
             * x-select selected event handler
             * @param  {string} value [selected value]
             * @param  {object} field [feedbackField]
             */
            onXSelectSelected(value, field) {
                field.value = value;
                field.domainKeyValue = value;
            },

            /**
             * x-detaillist input event handler
             * @param  {string} value [input value]
             * @param  {object} field [feedbackField]
             */
            onXDatailListInput(value, field) {
                let domainFieldValid = true;
                field.value = value;
                field.domainKeyValue = value;
                if (
                    field.domainKeyValue &&
                    field.domain &&
                    field.domainType === DOMAIN_TYPE.SUGGESTIONLIST
                ) {
                    domainFieldValid = field.domain.keyvalues.find((d) => {
                        return (
                            d.value.toLowerCase() ===
                            field.domainKeyValue.toLowerCase()
                        );
                    });
                }
                if (!domainFieldValid && field.value.length > 0) {
                    field.state = false;
                    field.invalidFieldError =
                        'Dit veld heeft geen geldige waarde (kies een waarde uit de lijst)';
                } else {
                    field.state = true;
                    field.invalidFieldError = '';
                }
                this.$forceUpdate();
            },

            /**
             * Set default values for input fields
             */
            $_setDefaultFeedbackFieldValues() {
                if (this.feedbackData.feedbackTheme?.feedbackType) {
                    const feedbackFields =
                        this.feedbackData.feedbackTheme.feedbackType.feedbackFields;
                    if (feedbackFields && feedbackFields.length > 0) {
                        this.$_setDefaultSuggestionListValues();
                        this.$_setDefaultFieldValues();
                    }
                }
            },

            /**
             * Set default values for suggestion list
             */
            $_setDefaultSuggestionListValues() {
                this.editFeedbackFields
                    .filter(
                        (ff) =>
                            ff.defaultValue &&
                            ff.domain &&
                            ff.domainType === DOMAIN_TYPE.SUGGESTIONLIST
                    )
                    .forEach((field) => {
                        const keyValue = field.domain.keyvalues.find(
                            ({ key }) => key === field.defaultValue
                        );
                        if (keyValue) {
                            field.domainKeyValue = keyValue.value;
                        }
                    });
            },

            /**
             * Set default values for fields with no domains and dropdownlists
             */
            $_setDefaultFieldValues() {
                this.editFeedbackFields
                    .filter(
                        (ff) =>
                            ff.defaultValue &&
                            (!ff.domain ||
                                ff.domainType === DOMAIN_TYPE.DROPDOWNLIST)
                    )
                    .forEach((field) => {
                        field.value =
                            field.type === FIELD_TYPE.NUMERIC
                                ? parseInt(field.defaultValue)
                                : field.defaultValue;
                    });
                // After default values have been set, calculate fields.
                this.$_calculateFields();
            },

            /**
             * Get selectopn options values from categories lookups
             * @param categoriesLookups
             */
            $_getCategoryValues(categoriesLookups) {
                const values = [];
                categoriesLookups.forEach((categoryLookup) => {
                    values.push({
                        value: categoryLookup.feedbackCategory.name,
                        text: categoryLookup.feedbackCategory.name
                    });
                });
                return values;
            },

            /**
             * Get b-form-select option values from domain values
             * @param  {array} keyvalues  [domain values]
             * @return {array}            [b-form-select option values]
             */
            $_getDomainValues(keyvalues) {
                const values = [];
                keyvalues.forEach((item) => {
                    values.push({
                        value: item.key,
                        text: item.value
                    });
                });

                return values.sort((a, b) => a.text.localeCompare(b.text));
            },
            $_getMinMaxValue(field, type) {
                if (type === 'min' && field.minValue) {
                    return field.minValue;
                }
                if (type === 'max' && field.maxValue) {
                    return field.maxValue;
                }
                return getMinMaxValueForInputNumber(
                    this.$_getMaxLength(field),
                    type
                );
            },
            /**
             * Corrects the length of input to PostGreSQL max integer
             * @param field
             */
            $_getMaxLength(field) {
                if (field.maxLength) {
                    if (
                        field.type === FIELD_TYPE.NUMERIC &&
                        field.maxLength > MAX_LENGTH_CONSTRAINTS.NUMERIC
                    ) {
                        field.maxLength = MAX_LENGTH_CONSTRAINTS.NUMERIC;
                    } else if (
                        field.type === FIELD_TYPE.NUMERICWITHDECIMALS &&
                        field.maxLength > MAX_LENGTH_CONSTRAINTS.NUMERICWITHDECIMALS
                    ) {
                        field.maxLength =
                            MAX_LENGTH_CONSTRAINTS.NUMERICWITHDECIMALS;
                    } else if (
                        field.type === FIELD_TYPE.ALPHANUMERIC &&
                        field.maxLength > MAX_LENGTH_CONSTRAINTS.ALPHANUMERIC
                    ) {
                        field.maxLength = MAX_LENGTH_CONSTRAINTS.ALPHANUMERIC;
                    }
                }
                return field.maxLength;
            },

            /**
             * Image file selected event handler
             * Lookup element in feedbackFields and set image data
             * @param  {object} image        [image data]
             * @param  {string} fieldname    [feedbackField name]
             */
            $_onImageSelected(image, fieldname) {
                const element = this.editFeedbackFields.find((item) => {
                    return item.name === fieldname;
                });
                if (element) {
                    this.showImagePreview = false;
                    element.value = image.data;
                    element.fileType = image.type;
                    element.state = true;
                    this.$forceUpdate();
                }
            },

            /**
             * Image file selected event handler
             * Lookup element in feedbackFields and set image data
             * @param  {object} image        [image data]
             * @param  {string} fieldname    [feedbackField name]
             */
            $_onImageRemoved(fieldname) {
                const element = this.editFeedbackFields.find((item) => {
                    return item.name === fieldname;
                });
                if (element) {
                    element.value = null;
                }
            },

            $_getUserName() {
                if (this.isMobile) {
                    return this.userInfo.userName;
                } else {
                    if (this.isMeasure) {
                        return this.userInfo.sessionId;
                    } else {
                        return 'web-feedback';
                    }
                }
            },

            /**
             * Feedback data compose start point
             * @return {object} [partial feedback data object]
             */
            _baseFeedbackData() {
                const feedbackTheme = this.isMobile
                    ? this.feedbackData.feedbackTheme.feedbackType
                    : this.feedbackData.feedbackTheme;

                const organizationId = this.isMobile
                    ? this.userInfo.organizationId
                    : this.getOrganizationId;

                return getCreateFeedbackData({
                    origFeedbackFields: this.editFeedbackFields,
                    feedbackTheme,
                    organizationId,
                    featureId: this.featureId,
                    location: this.feedbackShape.featureObject.location,
                    geometryType: this.geometryType,
                    gpsLocation: this.getMainMap.gpsTracker.getPosition(),
                    userId: this.isMobile ? this.userInfo.id : -1,
                    userName: this.$_getUserName()
                });
            },
            /**
             * Set domain list field values
             */
            _setDomainListFieldValues() {
                for (const field of this.editFeedbackFields) {
                    if (
                        field.domain &&
                        field.domainType === DOMAIN_TYPE.SUGGESTIONLIST
                    ) {
                        // Process suggestionlist
                        if (field.domainKeyValue) {
                            const foundDomain = field.domain.keyvalues.find((d) => {
                                return (
                                    d.value.toLowerCase() ===
                                    field.domainKeyValue.toLowerCase()
                                );
                            });
                            if (foundDomain) {
                                field.value = foundDomain.key;
                            }
                        } else {
                            field.value = '';
                        }
                    } else if (
                        field.domain &&
                        field.domainType === DOMAIN_TYPE.DROPDOWNLIST &&
                        field.value === null
                    ) {
                        // Process dropdownlist
                        field.value = '';
                    }
                }
            },

            $_isFieldNotRequiredAndEmpty(field) {
                return !field.required && (!field.value || field.value === '');
            },

            /**
             * Validate a field value.
             * Set the field.state to false if not valide.
             * @param  {object} field [field object]
             */
            _validateFieldValue(feedbackData, field) {
                switch (field.type) {
                    case FIELD_TYPE.FOTO:
                        this.$_validatePhotoValue(field);
                        break;
                    case FIELD_TYPE.DATE:
                        // No validation on non-required fields that are empty
                        if (this.$_isFieldNotRequiredAndEmpty(field)) {
                            break;
                        } else if (!validateDate(field.value)) {
                            field.state = false;
                        }
                        break;
                    case FIELD_TYPE.NUMERIC:
                        // No validation on non-required fields that are empty
                        if (
                            !field.required &&
                            !field.value &&
                            field.value !== undefined
                        ) {
                            break;
                        }
                        // No validation on personal data if it is not sent in the feedback
                        if (
                            !feedbackData.type.usePersonalData &&
                            field.personalData
                        ) {
                            break;
                        } else if (!validateNumber(field.value)) {
                            field.state = false;
                        }
                        break;
                    case FIELD_TYPE.EMAIL:
                        // No validation on non-required fields that are empty
                        if (this.$_isFieldNotRequiredAndEmpty(field)) {
                            break;
                        } else if (!validateEmail(field.value)) {
                            field.state = false;
                        }
                        break;
                }
                return field;
            },

            $_validatePhotoValue(field) {
                // find corresponding element in template for showing error
                const element = this.editFeedbackFields.find((item) => {
                    return item.name === field.name;
                });
                if (element) {
                    element.state = true;
                }
                field.state = true;
                if (field.required && !field.value) {
                    field.state = false;
                    if (element) {
                        element.state = false;
                    }
                }
            },

            $_validateEmailValue(field) {
                if (field.type === FIELD_TYPE.EMAIL) {
                    if (!validateEmail(field.value)) {
                        field.state = false;
                    } else {
                        field.state = true;
                    }
                    this.$forceUpdate();
                }
            },

            $_validateDate(field) {
                if (field.type === FIELD_TYPE.DATE) {
                    if (field.value.length > 0) {
                        field.state = validateDate(field.value);
                    } else {
                        field.state = null;
                    }
                    this.invalidFieldValueErrorMessage =
                        field.state === true || field.state === null
                            ? ''
                            : this.$t('labelInvalidDate');
                }
            },

            /**
             * Compose the feedback data object wich is send to the backend
             * @return {object} [feedback data object]
             */
            _composeFeedbackData() {
                let feedbackData = this._baseFeedbackData();
                for (let field of feedbackData.type.feedbackFields) {
                    const validatedField = this._validateFieldValue(
                        feedbackData,
                        field
                    );
                    if (
                        validatedField.state !== undefined &&
                        validatedField.state === false
                    ) {
                        this.$forceUpdate();
                        feedbackData = null;
                        this.$_showError();
                        break;
                    } else {
                        field.name = field.name.toLowerCase();
                    }

                    // To check for changed values at the backend, add the oldValue for category, state and foto fields.
                    if (field.type === FIELD_TYPE.CATEGORY) {
                        field.oldValue = this.origCategory.value;
                    }
                    if (field.type === FIELD_TYPE.STATE) {
                        field.oldValue = this.origState.value;
                    }
                    if (field.type === FIELD_TYPE.FOTO) {
                        field.oldValue = this.origFotoValue[field.name];
                    }
                }
                return feedbackData;
            },

            /**
             * Set the field value of any 'auto data' fields.
             */
            $_setValueAutoDataFields() {
                // web-feedback is anonymous
                this.anonymousFeedback = true;
                // Get measure totals
                const measureData = this.feedbackShape.measureTotals;
                for (const feedbackField of this.editFeedbackFields) {
                    this.$_setValueAutoDataField(feedbackField, measureData);
                }
                this.$_calculateFields();
            },

            $_setValueAutoDataField(feedbackField, measureData) {
                // Feedback Date field
                if (feedbackField.type === FIELD_TYPE.DATE) {
                    // Reset the Date value to local date format if it contains a 'Z'
                    if (feedbackField.value?.indexOf('Z') > -1) {
                        feedbackField.value = new Date(
                            feedbackField.value
                        ).toLocaleDateString(this.getLocale);
                    }
                }
                // Feedback address
                if (
                    feedbackField.type === FIELD_TYPE.GENERIC &&
                    feedbackField.name === 'adres'
                ) {
                    // Set the address value
                    feedbackField.value = this.lastFeedbackAddress;
                }
                // Creator email addres
                if (
                    feedbackField.personalData &&
                    feedbackField.type === FIELD_TYPE.EMAIL &&
                    feedbackField.value
                ) {
                    // Validation of email address value is done later on in feedbackService
                    // Feedback is not anonymous
                    this.anonymousFeedback = false;
                }
                // Fill default value for new feedbacks in fields with NULL values and either 1)state type or 2) invisible fields,
                if (
                    !this.feedbackModify &&
                    feedbackField.value === null &&
                    feedbackField.defaultValue
                ) {
                    // Set default value for state fields
                    // Set default value for invisible fields
                    if (
                        feedbackField.type === FIELD_TYPE.STATE ||
                        !feedbackField.visible
                    ) {
                        feedbackField.value = feedbackField.defaultValue;
                    }
                }
                // Fill measure data
                this._setValueAutoDataFieldMeasure(feedbackField, measureData);
            },

            _setValueAutoDataFieldMeasure(feedbackField, measureData) {
                if (measureData) {
                    if (feedbackField.name === 'length') {
                        feedbackField.value = measureData.length;
                    } else if (feedbackField.name === 'area') {
                        feedbackField.value = measureData.area;
                    } else if (feedbackField.name === 'coord_x') {
                        feedbackField.value = measureData.coord_x;
                    } else if (feedbackField.name === 'coord_y') {
                        feedbackField.value = measureData.coord_y;
                    }
                }
            },

            /**
             * Perform a browser validity check
             * @return {string} [error string if not valide, otherwise null]
             */
            _validateUserInput() {
                const fieldsEmptyMessage = 'Niet alle velden zijn ingevuld';
                // reset error
                let error = null;
                // validate form
                if (
                    this.$refs.feedbackForm &&
                    !this.$refs.feedbackForm.checkValidity()
                ) {
                    error = fieldsEmptyMessage;
                }
                if (this.errorInUse) {
                    error = fieldsEmptyMessage;
                }
                return error;
            },

            /**
             * Create a feedback in mobile or web.
             * @param  {object} feedbackData [feedback data to save]
             * @return {object}              [promise object]
             */

            async _createFeedback(data) {
                let name = '';
                if (data.id === '-1') {
                    name = this.isMobile ? 'CreateFeedback' : 'CreateWebFeedback';
                } else {
                    name = 'UpdateFeedback';
                }

                const result = await this.$store.dispatch('useApiData', {
                    name: name,
                    data: data
                });
                return result.data;
            },

            /**
             * Save feedback data with a call to the API
             * @param  {object} feedbackData [feedback data object]
             * @return {[type]}              [description]
             */
            async _saveFeedbackData(feedbackData) {
                this.isSaving = true;
                try {
                    // save feedback
                    const result = await this._createFeedback(feedbackData);
                    // update domains in state
                    const fields = feedbackData.type.feedbackFields.filter(
                        ({ domainExtendable }) => domainExtendable
                    );
                    this.$store.commit('UPDATE_FEEDBACKFIELDS_DOMAIN', {
                        id: this.feedbackThemeObject.id,
                        fields
                    });

                    // add form input to search cache
                    let name = 'HarvestAttributes';
                    if (!this.isMobile) {
                        name = 'WebHarvestAttributes';
                    }
                    await this.$store.dispatch('useApiData', {
                        name: name,
                        data: {
                            feedbackTypeId: result.type.id,
                            feedbackFields: result.type.feedbackFields
                        }
                    });

                    if (this.isMobile || (!this.isMobile && this.isMeasure)) {
                        // Show success message
                        // if it is a new feedback, the message comes below the (feedback) buttons,
                        // by changing a feedback almost at the top with a small marge
                        this.$store.commit('SET_ALERT_MESSAGE', {
                            message: this.$t('feedbackProcessed'),
                            alertClass: feedbackData.id === '-1' ? 'lower' : null,
                            variant: 'success',
                            optional: false,
                            hasSearch: false
                        });

                        // hide message with delay
                        setTimeout(() => {
                            this.$store.commit('SET_ALERT_MESSAGE', {
                                message: null,
                                alertClass: null,
                                variant: null,
                                optional: null,
                                hasSearch: null
                            });
                        }, 5000);
                    }

                    // Web feedback
                    if (!this.isMobile && !this.isMeasure) {
                        // Retrieve feedbackNumber
                        const feedbackNumber = result.type.feedbackFields.find(
                            (ff) => ff.name === 'meldingsnummer'
                        ).value;
                        // Show alert that feedback is saved
                        this.$_showAlertFeedBackSaved(
                            this.anonymousFeedback,
                            feedbackNumber
                        );
                    }
                    this.isSaving = false;
                    //#1606 adds measurement point for insights
                    this.$store.dispatch('trackRapportageEvent', {
                        name: 'saveFeedback'
                    });

                    let feedbackDataArray = [];
                    feedbackDataArray.push(feedbackData);
                    // close form
                    this.$emit('finished', {
                        feedbackData: feedbackDataArray,
                        themeObject: this.feedbackThemeObject
                    });
                } catch (error) {
                    this.showToast({
                        title: this.$t('saveFailureTitle'),
                        message: this.$t(error.message),
                        variant: 'danger',
                        autoHideDelay: 10000
                    });
                }
                this.isSaving = false;
            },

            /**
             * Show alert that user saved web feedback
             * @param {*} anonymous, user entered email (anonymous === false) or did not entered email (anonymous === true)
             * @param {number} feedbackNumber, the feedbackNumber of the newly created feedback
             */
            $_showAlertFeedBackSaved(anonymous, feedbackNumber) {
                let extraText = '';

                if (!anonymous) {
                    extraText = `${this.$t('mailCheckSpam')} `;
                }

                const alertText = this.$t('feedbackSendConfirmation', [
                    `${feedbackNumber}`,
                    `${extraText}`
                ]);
                this.$store.commit('ADD_STATUS_MESSAGE', {
                    message: alertText,
                    variant: 'success',
                    autoHideDelay: 5000
                });
            },

            /**
             * Save new or modified feedback
             */
            $_onSubmit() {
                // Browser check input
                this.error = this._validateUserInput();
                if (this.error) {
                    this.$_showError();
                    return;
                }
                if (this.isMultiFeedbackEdit) {
                    this.$_saveMultiFeedbacks();
                    return;
                }
                // Look for auto-data fields
                this.$_setValueAutoDataFields(this.feedbackData);

                // Set key value for domain lists
                this._setDomainListFieldValues();

                // get feedback data
                const feedbackData = this._composeFeedbackData();
                if (feedbackData) {
                    this._saveFeedbackData(feedbackData);
                }
            },
            async $_saveMultiFeedbacks() {
                this.isSaving = true;

                // get feedback data
                const feedbackMutations = this.$_updateMultiFeedbacksData();

                // API expects and object with an attribute "feedbacks" containing an array of feedbacks
                const feedbackData = {
                    feedbacks: feedbackMutations.feedbacks
                };
                try {
                    // Save when there have actually been values entered by the user.
                    if (feedbackMutations.valuesChanged) {
                        await this.$store.dispatch('useApiData', {
                            name: 'UpdateFeedbacks',
                            data: feedbackData
                        });
                        this.$store.commit('SET_ALERT_MESSAGE', {
                            message: this.$t('feedbackProcessed'),
                            alertClass: null,
                            variant: 'success',
                            optional: false,
                            hasSearch: false,
                            autoHideDelay: 10000
                        });
                    }
                    // Exit with full list of feedbacks
                    this.$emit('finished', {
                        feedbackData: feedbackMutations.feedbacks,
                        themeObject: this.feedbackThemeObject
                    });
                } catch (error) {
                    this.showToast({
                        title: this.$t('saveFailureTitle'),
                        message: this.$t(error.message),
                        variant: 'danger',
                        autoHideDelay: 10000
                    });
                }

                this.isSaving = false;
            },

            $_updateMultiFeedbacksData() {
                // prepare feedback data to be saved.
                // find fields that the user has entered and add these values to the feedbacks in the selected feedback list
                // Return an object with the prepared data and a flag that indicates if there has actually been an entered field

                let valuesChanged = false;
                // changed items copy to original feedbacks
                this.editFeedbackFields.forEach((f) => {
                    if (!f.personalData) {
                        if (f.value && f.type !== FIELD_TYPE.GENERIC) {
                            // Set flag that an entered field has been found
                            valuesChanged = true;
                            this.feedbackShape.featureObject.features.map(
                                (feature) => {
                                    feature.values_[f.name] = f.value;
                                }
                            );
                        }
                    }
                });

                let feedbacks = [];
                let feedbackData = this._baseFeedbackData();
                this.feedbackShape.featureObject.features.map((f) => {
                    let newFeedback = JSON.parse(JSON.stringify(feedbackData));
                    newFeedback.id = f.id_;

                    // Add location value to make screen behave properly after returning to calling component
                    newFeedback.location = getLocationString(f);
                    newFeedback.type.feedbackFields.map((field) => {
                        field.value = f.values_[field.name];
                        return field;
                    });
                    newFeedback.type.geometryType = f.getGeometry().getType();
                    newFeedback.geometryType = newFeedback.type.geometryType;
                    feedbacks.push(newFeedback);
                });

                return { feedbacks: feedbacks, valuesChanged: valuesChanged };
            },
            $_updateDomainValue(field, value) {
                if (field.domainType === DOMAIN_TYPE.DROPDOWNLIST) {
                    field.value = value;
                } else if (field.domainType === DOMAIN_TYPE.SUGGESTIONLIST) {
                    field.domainKeyValue = value;
                }
            },
            $_updateKeyvalues(field, newValue) {
                field.domain.keyvalues = newValue;
            },
            /**
             * clicked on button "Locatie wijzigen"
             */
            $_onClickSetLocationButton() {
                this.$emit('setLocation', {
                    statusMessage:
                        this.geometryType === FEEDBACKS_GEOMETRY_TYPES.POINT
                            ? this.$t('hintChangePoint')
                            : this.$t('hintChangeLinePolygon'),
                    editFeedbackFields: this.editFeedbackFields
                });
            },

            /**
             * User check/uncheck personal data checkbox
             * @param  {object} $event [event data]
             */
            $_changeUsePersonalData(event) {
                if (event === false) {
                    // clear personal data fields
                    this.editFeedbackFields.forEach((field) => {
                        if (field.personalData) {
                            field.state = true;
                            field.value = null;
                        }
                    });
                }
            },

            /**
             * Cancel add/modify feedback
             */
            $_onCancel(event) {
                this.$emit('cancel');
            },

            $_showError() {
                this.showToast({
                    message: this.$t('incorrectInputAllFields'),
                    variant: 'danger',
                    autoHideDelay: 10000
                });
            },
            showToast(message) {
                this.$store.commit('ADD_STATUS_MESSAGE', message);
            },

            /**
             * Returns a boolean based on field attributes
             * @param field
             */
            $_tryFindDomain(field) {
                return (
                    (field.type === FIELD_TYPE.ALPHANUMERIC ||
                        field.type === FIELD_TYPE.STATE) &&
                    field.domain &&
                    field.domain.keyvalues &&
                    field.domain.keyvalues.length > 0 &&
                    (field.domainType === DOMAIN_TYPE.DROPDOWNLIST ||
                        field.domainType === DOMAIN_TYPE.SUGGESTIONLIST)
                );
            },

            $_initFieldValueForAlphanumericType(field) {
                if (
                    field.type === FIELD_TYPE.ALPHANUMERIC &&
                    field.domain &&
                    field.domain.keyvalues &&
                    field.domain.keyvalues.length > 0
                ) {
                    field.domainKeyValue = null;
                }
            },

            /**
             * Initialize feedback data for a existing feedback (modify)
             * @param  {object} modifyFeedbackData [existing feedback data]
             */
            $_initializeModifyFeedbackData() {
                // set id
                // modify feedback field initialisation
                const feature = this.feedbackShape.featureObject.feature;
                this.featureId = feature.getId();
                this.editFeedbackFields.forEach((field) => {
                    // Als eerste in de properties lijst
                    if (!this.isMultiFeedbackEdit) {
                        const foundItem = this.feedbackShape.data[field.name];
                        if (foundItem) {
                            field.value = foundItem.value;
                        }
                        // Ten tweede in de values lijst
                        if (field.name in feature.values_) {
                            field.value = feature.values_[field.name];
                        }
                    } else {
                        field.value = '';
                    }
                    this.$nextTick(() => {
                        // post process
                        if (field.value) {
                            // alter field values
                            if (this.$_tryFindDomain(field)) {
                                // find domain value case insensitive
                                const foundDomain = field.domain.keyvalues.find(
                                    (d) => {
                                        return (
                                            d.key.toLowerCase() ===
                                            field.value.toLowerCase()
                                        );
                                    }
                                );
                                if (foundDomain) {
                                    field.domainKeyValue = foundDomain.value;
                                }
                            } else if (field.type === FIELD_TYPE.DATE) {
                                if (!moment(field.value, 'DD-MM-YYYY').isValid()) {
                                    field.value = moment(field.value).format(
                                        'DD-MM-YYYY'
                                    );
                                }
                            }
                        } else {
                            this.$_initFieldValueForAlphanumericType(field);
                        }
                    });
                });
            },
            async $_getAddress(location) {
                let address = 'Geen adres gevonden.';
                if (this.feedbackModify && !this.hasChangedShape) {
                    const addressField = this.editFeedbackFields.find(
                        (field) => field.name === 'adres'
                    );
                    if (addressField && addressField.value) {
                        return addressField.value;
                    }
                }
                // Get address-info for this location (call web-api)
                const addressInfo = await this.getAddressInfo([location]);
                // Check if real address was found
                if (
                    addressInfo &&
                    addressInfo.data &&
                    addressInfo.data.streetName
                ) {
                    address = `${addressInfo.data.displayName}`;
                }
                return address;
            },

            $_calculateField(field) {
                var fieldNames = getVariablesFromString(field.expression);

                // For every fieldname in the calculation
                // try and get the value and put it in the valueObject.
                // Stop when not all fields have a value.
                let valueObject = {};
                let allValues = true;

                for (const fieldName of fieldNames) {
                    const formField = this.editFeedbackFields.find(
                        (f) => f.name === fieldName
                    );

                    if (formField.value === null || formField.value === '') {
                        allValues = false;
                        break;
                    }
                    valueObject[fieldName] = formField.value;
                }

                if (!allValues) {
                    field.value = null;
                } else {
                    field.value = evaluateNumericExpression(
                        field.expression,
                        valueObject
                    );
                }
            },

            $_calculateFields() {
                // Find calculatedfields, if any calculate.
                const calculatedFields = this.editFeedbackFields.filter(
                    (f) => f.type === this.fieldTypeConst.EXPRESSION
                );

                for (const calculatedField of calculatedFields) {
                    this.$_calculateField(calculatedField);
                }
            },
            // This function gets called when a numeric field value has changed.
            // If the form contains calculated fields, recalculate their values.
            $_updateCalculatedFields(value, field) {
                field.value = value;

                this.$forceUpdate();
                this.$_calculateFields();
            },

            $_invalidExpression(field) {
                if (isNaN(field.value) || !isFinite(field.value)) {
                    return this.$t('errorExpressionCalculation');
                }

                return null;
            }
        }
    };
</script>

<style lang="scss" scoped>
.modal-content {
    height: 100%;
    border: none;
    border-radius: initial;
}

.modal-body {
    padding: 0px;
    height: 100%;
}

.personal-data {
    padding-top: 10px;
}

.sticky-bottom {
    left: auto;
    background-color: #fff;
    padding-right: 40px;
    padding-left: 0px;
    padding-bottom: 20px;
}
</style>
